import React, { useRef, useEffect } from "react";
import { Modal, ModalDialog, Typography, Divider, Button, Box } from "@mui/joy";
import Cropper from "cropperjs";

import "cropperjs/dist/cropper.css";

interface ImageCropModalProps {
  open: boolean;
  initialImage: string | null; // New prop to receive the initial image from the parent
  onClose: () => void;
  onCrop: (croppedImage: string) => void;
}

const ImageCropModal: React.FC<ImageCropModalProps> = ({ open, initialImage, onClose, onCrop }) => {
  const imageRef = useRef<HTMLImageElement | null>(null);
  const cropperInstanceRef = useRef<Cropper | null>(null);

  useEffect(() => {
    // Destroy any existing Cropper instance
    if (cropperInstanceRef.current) {
      cropperInstanceRef.current.destroy();
      cropperInstanceRef.current = null;
    }

    // Initialize Cropper if the modal is open and an image is provided
    if (imageRef.current && initialImage && open) {
      cropperInstanceRef.current = new Cropper(imageRef.current, {
        aspectRatio: 1,
        viewMode: 1,
        dragMode: "move",
        scalable: true,
        zoomable: true,
        autoCropArea: 1,
      });
    }

    // Cleanup when the modal is closed or a new image is selected
    return () => {
      cropperInstanceRef.current?.destroy();
      cropperInstanceRef.current = null;
    };
  }, [initialImage, open]); // Only re-run when `initialImage` or `open` changes

  const handleCrop = () => {
    if (cropperInstanceRef.current) {
      const croppedCanvas = cropperInstanceRef.current.getCroppedCanvas({
        width: 200,
        height: 200,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: "high",
      });
      const croppedDataUrl = croppedCanvas.toDataURL("image/png");
      onCrop(croppedDataUrl); // Send the cropped image back to the parent
      onClose(); // Close the modal
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ maxWidth: 400, padding: 2 }}>
        <Typography level="h4">Adjust Image</Typography>
        <Divider sx={{ my: 2 }} />
        <Box display="flex" flexDirection="column" alignItems="center">
          {initialImage && (
            <div
              style={{
                width: 300,
                height: 300,
                overflow: "hidden",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                ref={imageRef}
                src={initialImage}
                alt="Crop Preview"
                onLoad={() => {
                  // Delay Cropper initialization until image is loaded
                  if (imageRef.current && open && !cropperInstanceRef.current) {
                    cropperInstanceRef.current = new Cropper(imageRef.current, {
                      aspectRatio: 1,
                      viewMode: 1,
                      dragMode: "move",
                      scalable: true,
                      zoomable: true,
                      autoCropArea: 1,
                    });
                  }
                }}
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
              />
            </div>
          )}
          <Box display="flex" gap={2} mt={2} style={{ width: "100%" }} justifyContent="space-between">
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleCrop}>Crop Image</Button>
          </Box>
        </Box>
      </ModalDialog>
    </Modal>
  );
};

export default ImageCropModal;
